import { BusUserLoginAuthenticator } from '@main/auth/bus-user-login-authenticator';
import { BusUserLogoutAuthenticator } from '@main/auth/bus-user-logout-authenticator';
import { PublisherFactory } from '@main/factories/publisher.factory';
import { RepositoryFactory } from '@main/factories/repository.factory';
import { ValidatorFactory } from '@main/factories/validator.factory';
import { CrmToAircallStatusMapper } from '@main/mappers/crm-to-aircall-status.mapper';
import type { AcceptIncomingCallUsecase } from '@usecases/accept-incoming-call.usecase';
import { AcceptIncomingCall } from '@usecases/accept-incoming-call.usecase';
import type { ConnectedCallUsecase } from '@usecases/connected-call.usecase';
import {
  ConnectedCall,
  ConnectedCallWithCrmOps,
} from '@usecases/connected-call.usecase';
import type { DialNumberUsecase } from '@usecases/dial-number.usecase';
import { DialNumber } from '@usecases/dial-number.usecase';
import type { DisconnectedCallUsecase } from '@usecases/disconnected-call.usecase';
import { DisconnectedCall } from '@usecases/disconnected-call.usecase';
import type { GetSignedRecordingUrlUsecase } from '@usecases/get-signed-recording-url.usecase';
import { GetSignedRecordingUrl } from '@usecases/get-signed-recording-url.usecase';
import type { HangupCallUsecase } from '@usecases/hangup-call.usecase';
import { HangupCall } from '@usecases/hangup-call.usecase';
import type { HoldToggleUsecase } from '@usecases/hold-toggle.usecase';
import { HoldToggle } from '@usecases/hold-toggle.usecase';
import type { IdleCallUsecase } from '@usecases/idle-call.usecase';
import { IdleCall } from '@usecases/idle-call.usecase';
import type { MuteToggleUsecase } from '@usecases/mute-toggle.usecase';
import { MuteToggle } from '@usecases/mute-toggle.usecase';
import type { RecordCallUuidUsecase } from '@usecases/record-call-uuid.usecase';
import { RecordCallUuid } from '@usecases/record-call-uuid.usecase';
import type { RejectIncomingCallUsecase } from '@usecases/reject-incoming-call.usecase';
import { RejectIncomingCall } from '@usecases/reject-incoming-call.usecase';
import {
  RingIncomingCall,
  RingIncomingCallWithCrmOps,
} from '@usecases/ring-incoming-call.usecase';
import type { SetOutboundNumberUsecase } from '@usecases/set-outbound-number.usecase';
import { SetOutboundNumber } from '@usecases/set-outbound-number.usecase';
import type { StatusSyncAircallToCrmUsecase } from '@usecases/status-sync-aircall-to-crm.usecase';
import { StatusSyncAircallToCrm } from '@usecases/status-sync-aircall-to-crm.usecase';
import type { StatusSyncCrmToAircallUsecase } from '@usecases/status-sync-crm-to-aircall.usecase';
import { StatusSyncCrmToAircall } from '@usecases/status-sync-crm-to-aircall.usecase';
import type { StoreIntegrationSettingsUsecase } from '@usecases/store-integration-settings.usecase';
import { StoreIntegrationSettings } from '@usecases/store-integration-settings.usecase';
import type { SyncIntegrationSettingUpdateUsecase } from '@usecases/sync-integration-setting-update.usecase';
import { SyncIntegrationSettingUpdate } from '@usecases/sync-integration-setting-update.usecase';
import type { SyncParticipantsUsecase } from '@usecases/sync-participants.usecase';
import { SyncParticipants } from '@usecases/sync-participants.usecase';
import { UserLogin } from '@usecases/user-login.usecase';
import { UserLogout } from '@usecases/user-logout.usecase';
import { WrapUpTimeEndedUseCase } from '@usecases/wrap-up-time-ended.usecase';
import { WrapUpTimeStartedUseCase } from '@usecases/wrap-up-time-started.usecase';
import type { SupervisorDisconnectUsecase } from '../../call-monitor/supervisorDisconnect/supervisor-disconnect.usecase';
import { SupervisorDisconnect } from '../../call-monitor/supervisorDisconnect/supervisor-disconnect.usecase';
import type { SupervisorListenUsecase } from '../../call-monitor/supervisorListening/supervisor-listening.usecase';
import { SupervisorListen } from '../../call-monitor/supervisorListening/supervisor-listening.usecase';
import type { RecordingToggleUsecase } from '../../call-recording/recording-toggle.usecase';
import { RecordingToggle } from '../../call-recording/recording-toggle.usecase';
import type { BlindTransferNumberUsecase } from '../../callTransfer/blindTransfer/blind-transfer-call.usecase';
import { BlindTransferNumber } from '../../callTransfer/blindTransfer/blind-transfer-call.usecase';
import type { ConnectedTransferCallUsecase } from '../../callTransfer/connect-merge-transfer/connect-merge-transfer-call.usecase';
import {
  ConnectedTransferCall,
  ConnectedTransferCallWithCrmOps,
} from '../../callTransfer/connect-merge-transfer/connect-merge-transfer-call.usecase';
import type { DisconnectedTransferCallUsecase } from '../../callTransfer/disconnect-transfer/disconnect-transfer-call.usecase';
import { DisconnectedTransferCall } from '../../callTransfer/disconnect-transfer/disconnect-transfer-call.usecase';
import type { HangupTransferCallUsecase } from '../../callTransfer/hangup-transfer/hangup-transfer-call.usecase';
import { HangupTransferCall } from '../../callTransfer/hangup-transfer/hangup-transfer-call.usecase';
import type { MuteAgentCallUsecase } from '../../callTransfer/MuteAgent/mute-agent.usecase';
import { MuteAgent } from '../../callTransfer/MuteAgent/mute-agent.usecase';
import { ShowAgentList } from '../../callTransfer/showAgentList/show-agentlist.usecase';
import { StoreAgentList } from '../../callTransfer/storeAgentList/store-agentlist.usecase';
import type { SwapCallUsecase } from '../../callTransfer/SwapCall/swap-call.usecase';
import { SwapCall } from '../../callTransfer/SwapCall/swap-call.usecase';
import { SyncAgentStatus } from '../../callTransfer/syncAgentStatus/sync-agent-status.usecase';
import { TeammateAvailabilityChanged } from '../../callTransfer/teammateAvailabilityChanged/teammate-availability-changed.usecase';
import { WarmMergeTransfer } from '../../callTransfer/warmMergeTransfer/warm-merge-transfer.usecase';
import type { TransferNumberUsecase } from '../../callTransfer/warmTransfer/warm-transfer-call.usecase';
import { TransferNumber } from '../../callTransfer/warmTransfer/warm-transfer-call.usecase';
import { BusUserSSOLoginAuthenticator } from '../../sso/auth/bus-user-sso-login-authenticator';
import { UserSSOLogin } from '../../sso/usecases/user-sso-login.usecase';
import { BusUserUnifiedLoginAuthenticator } from '../../unifiedLogin/auth/bus-unified-login-authenticator';
import { UserUnifiedLogin } from '../../unifiedLogin/usecases/user-unified-login.usecase';
import type { FetchPrerecordedMessagesUsecase } from '../../voiceMailDrop/fetch-list-prerecorded-messages.usecase';
import { FetchPrerecordedMessages } from '../../voiceMailDrop/fetch-list-prerecorded-messages.usecase';
import type { VoiceMailDropUsecase } from '../../voiceMailDrop/voice-mail-drop.usecase';
import { VoiceMailDrop } from '../../voiceMailDrop/voice-mail-drop.usecase';
import { integrationManagerHttpClient } from '../http/integration-manager-http-client';
import { phoneHttpClient } from '../http/phone-http-client';
import { CrmOpsFactory } from './crm-ops.factory';
export class UsecaseFactory {
  private static ringIncomingCallUsecase: RingIncomingCall;

  static buildRingIncomingCallUsecase() {
    if (!this.ringIncomingCallUsecase) {
      this.ringIncomingCallUsecase = new RingIncomingCall(
        ValidatorFactory.buildRingIncomingCallValidator(),
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildRingIncomingCallPublisher(),
      );
    }

    return this.ringIncomingCallUsecase;
  }

  private static ringIncomingCallWithCrmOps: RingIncomingCallWithCrmOps;
  static buildRingIncomingCallWithCrmOps(): RingIncomingCallWithCrmOps {
    return (
      this.ringIncomingCallWithCrmOps ??
      (this.ringIncomingCallWithCrmOps = new RingIncomingCallWithCrmOps(
        CrmOpsFactory.buildSalesforceRingIncomingCallCrmOps(),
        ValidatorFactory.buildRingIncomingCallValidator(),
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildRingIncomingCallPublisher(),
      ))
    );
  }

  private static WrapUpTimeStartedUseCase: WrapUpTimeStartedUseCase;
  static buildWrapUpTimeStartedControllerUseCase(): WrapUpTimeStartedUseCase {
    return (
      this.WrapUpTimeStartedUseCase ??
      (this.WrapUpTimeStartedUseCase = new WrapUpTimeStartedUseCase(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildWrapUpTimeStartedPublisher(),
      ))
    );
  }

  private static WrapUpTimeEndedUseCase: WrapUpTimeEndedUseCase;
  static buildWrapUpTimeEndedControllerUseCase(): WrapUpTimeEndedUseCase {
    return (
      this.WrapUpTimeEndedUseCase ??
      (this.WrapUpTimeEndedUseCase = new WrapUpTimeEndedUseCase(
        PublisherFactory.buildWrapUpTimeEndedPublisher(),
      ))
    );
  }

  private static userLoginUsecase: UserLogin;
  static buildUserLoginUsecase(): UserLogin {
    return (
      this.userLoginUsecase ??
      (this.userLoginUsecase = new UserLogin(
        new BusUserLoginAuthenticator(),
        RepositoryFactory.buildMemUserRepository(),
        PublisherFactory.buildUserLoginPublisher(),
      ))
    );
  }

  private static userSSOLoginUsecase: UserSSOLogin;
  static buildUserSSOLoginUsecase(): UserSSOLogin {
    return (
      this.userSSOLoginUsecase ??
      (this.userSSOLoginUsecase = new UserSSOLogin(
        new BusUserSSOLoginAuthenticator(),
        RepositoryFactory.buildMemUserRepository(),
        PublisherFactory.buildUserLoginPublisher(),
      ))
    );
  }

  private static userUnifiedLoginUsecase: UserUnifiedLogin;
  static buildUserUnifiedLoginUsecase(): UserUnifiedLogin {
    return (
      this.userUnifiedLoginUsecase ??
      (this.userUnifiedLoginUsecase = new UserUnifiedLogin(
        new BusUserUnifiedLoginAuthenticator(),
        RepositoryFactory.buildMemUserRepository(),
        PublisherFactory.buildUserLoginPublisher(),
      ))
    );
  }

  private static userLogoutUsecase: UserLogout;
  static buildUserLogoutUsecase(): UserLogout {
    return (
      this.userLogoutUsecase ??
      (this.userLogoutUsecase = new UserLogout(
        new BusUserLogoutAuthenticator(),
        RepositoryFactory.buildMemUserRepository(),
        PublisherFactory.buildUserLogoutPublisher(),
      ))
    );
  }

  private static acceptIncomingCallUsecase: AcceptIncomingCallUsecase;
  static buildAcceptIncomingCallUsecase(): AcceptIncomingCallUsecase {
    return (
      this.acceptIncomingCallUsecase ??
      (this.acceptIncomingCallUsecase = new AcceptIncomingCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildAcceptIncomingCallPublisher(),
      ))
    );
  }

  private static hangupCallUsecase: HangupCallUsecase;
  static buildHangupCallUsecase(): HangupCallUsecase {
    return (
      this.hangupCallUsecase ??
      (this.hangupCallUsecase = new HangupCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildHangupCallPublisher(),
      ))
    );
  }

  private static hangupTransferCallUsecase: HangupTransferCallUsecase;
  static buildHangupTransferCallUsecase(): HangupTransferCallUsecase {
    return (
      this.hangupTransferCallUsecase ??
      (this.hangupTransferCallUsecase = new HangupTransferCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildHangupTransferCallPublisher(),
      ))
    );
  }

  private static muteToggleUsecase: MuteToggleUsecase;
  static buildMuteToggleUsecase(): MuteToggleUsecase {
    return (
      this.muteToggleUsecase ??
      (this.muteToggleUsecase = new MuteToggle(
        PublisherFactory.buildMuteTogglePublisher(),
      ))
    );
  }

  private static recordingToggleUsecase: RecordingToggleUsecase;
  static buildRecordingToggleUsecase(): RecordingToggleUsecase {
    return (
      this.recordingToggleUsecase ??
      (this.recordingToggleUsecase = new RecordingToggle(
        PublisherFactory.buildRecordingTogglePublisher(),
      ))
    );
  }

  private static syncParticipantsUsecase: SyncParticipantsUsecase;
  static buildSyncParticipantsUsecase(): SyncParticipantsUsecase {
    return (
      this.syncParticipantsUsecase ??
      (this.syncParticipantsUsecase = new SyncParticipants(
        ValidatorFactory.buildSyncParticipantsValidator(),
        RepositoryFactory.buildMemParticipantRepository(),
        RepositoryFactory.buildMemCallRepository(),
      ))
    );
  }

  private static holdToggleUsecase: HoldToggleUsecase;
  static buildHoldToggleUsecase(): HoldToggleUsecase {
    return (
      this.holdToggleUsecase ??
      (this.holdToggleUsecase = new HoldToggle(
        RepositoryFactory.buildMemCallRepository(),
        RepositoryFactory.buildMemParticipantRepository(),
        PublisherFactory.buildHoldTogglePublisher(),
      ))
    );
  }

  private static setOutboundNumberUsecase: SetOutboundNumberUsecase;
  static buildSetOutboundNumberUsecase(): SetOutboundNumberUsecase {
    return (
      this.setOutboundNumberUsecase ??
      (this.setOutboundNumberUsecase = new SetOutboundNumber(
        RepositoryFactory.buildMemUserRepository(),
      ))
    );
  }

  private static dialNumberUsecase: DialNumberUsecase;
  static buildDialNumberUsecase(): DialNumberUsecase {
    return (
      this.dialNumberUsecase ??
      (this.dialNumberUsecase = new DialNumber(
        RepositoryFactory.buildMemUserRepository(),
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildDialNumberPublisher(),
      ))
    );
  }

  private static connectedCallUsecase: ConnectedCallUsecase;
  static buildConnectedCallUsecase(): ConnectedCallUsecase {
    return (
      this.connectedCallUsecase ??
      (this.connectedCallUsecase = new ConnectedCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildConnectedCallPublisher(),
      ))
    );
  }

  private static connectedCallWithCrmOps: ConnectedCallWithCrmOps;
  static buildConnectedCallWithCrmOps(): ConnectedCallWithCrmOps {
    return (
      this.connectedCallWithCrmOps ??
      (this.connectedCallWithCrmOps = new ConnectedCallWithCrmOps(
        CrmOpsFactory.buildSalesforceConnectedCallCrmOps(),
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildConnectedCallPublisher(),
      ))
    );
  }

  private static disconnectedCallUsecase: DisconnectedCallUsecase;
  static buildDisconnectedCallUsecase(): DisconnectedCallUsecase {
    return (
      this.disconnectedCallUsecase ??
      (this.disconnectedCallUsecase = new DisconnectedCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildDisconnectedCallPublisher(),
        RepositoryFactory.buildMemParticipantRepository(),
      ))
    );
  }

  private static idleCallUsecase: IdleCallUsecase;
  static buildIdleCallUsecase(): IdleCallUsecase {
    return (
      this.idleCallUsecase ??
      (this.idleCallUsecase = new IdleCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildIdleCallPublisher(),
      ))
    );
  }

  private static rejectIncomingCallUsecase: RejectIncomingCallUsecase;
  static buildRejectIncomingCallUsecase(): RejectIncomingCallUsecase {
    return (
      this.rejectIncomingCallUsecase ??
      (this.rejectIncomingCallUsecase = new RejectIncomingCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildRejectIncomingCallPublisher(),
      ))
    );
  }

  private static statusSyncCrmToAircallUsecase: StatusSyncCrmToAircallUsecase;
  static buildStatusSyncCrmToAircallUsecase(): StatusSyncCrmToAircallUsecase {
    return (
      this.statusSyncCrmToAircallUsecase ??
      (this.statusSyncCrmToAircallUsecase = new StatusSyncCrmToAircall(
        RepositoryFactory.buildMemUserRepository(),
        RepositoryFactory.buildMemIntegrationRepository(),
        new CrmToAircallStatusMapper(),
        PublisherFactory.buildStatusSyncCrmToAircallPublisher(),
      ))
    );
  }

  private static statusSyncAircallToCrmUsecase: StatusSyncAircallToCrmUsecase;
  static buildStatusSyncAircallToCrmUsecase(): StatusSyncAircallToCrmUsecase {
    return (
      this.statusSyncAircallToCrmUsecase ??
      (this.statusSyncAircallToCrmUsecase = new StatusSyncAircallToCrm(
        RepositoryFactory.buildMemUserRepository(),
        RepositoryFactory.buildMemCallRepository(),
        RepositoryFactory.buildMemIntegrationRepository(),
        PublisherFactory.buildStatusSyncAircallToCrmPublisher(),
      ))
    );
  }

  private static storeIntegrationSettingsUsecase: StoreIntegrationSettingsUsecase;
  static buildStoreIntegrationSettingsUsecase(): StoreIntegrationSettingsUsecase {
    return (
      this.storeIntegrationSettingsUsecase ??
      (this.storeIntegrationSettingsUsecase = new StoreIntegrationSettings(
        phoneHttpClient,
        integrationManagerHttpClient,
        RepositoryFactory.buildMemIntegrationRepository(),
      ))
    );
  }

  private static transferNumberUsecase: TransferNumberUsecase;
  static buildTransferNumberUsecase(): TransferNumberUsecase {
    return (
      this.transferNumberUsecase ??
      (this.transferNumberUsecase = new TransferNumber(
        RepositoryFactory.buildMemUserRepository(),
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildTransferNumberPublisher(),
      ))
    );
  }

  private static blindTransferNumberUsecase: BlindTransferNumberUsecase;
  static buildBlindTransferNumberUsecase(): BlindTransferNumberUsecase {
    return (
      this.blindTransferNumberUsecase ??
      (this.blindTransferNumberUsecase = new BlindTransferNumber(
        RepositoryFactory.buildMemUserRepository(),
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildBlindTransferNumberPublisher(),
      ))
    );
  }

  private static warmMergeTransferUsecase: WarmMergeTransfer;
  static buildWarmMergeTransferUsecase(): WarmMergeTransfer {
    return (
      this.warmMergeTransferUsecase ??
      (this.warmMergeTransferUsecase = new WarmMergeTransfer(
        PublisherFactory.buildWarmMergeTransferPublisher(),
      ))
    );
  }

  private static connectedTransferCallUsecase: ConnectedTransferCallUsecase;
  static buildConnectedTransferCallUsecase(): ConnectedTransferCallUsecase {
    return (
      this.connectedTransferCallUsecase ??
      (this.connectedTransferCallUsecase = new ConnectedTransferCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildConnectedTransferCallPublisher(),
      ))
    );
  }

  private static disconnectedTransferCallUsecase: DisconnectedTransferCallUsecase;
  static buildDisconnectedTransferCallUsecase(): DisconnectedTransferCallUsecase {
    return (
      this.disconnectedTransferCallUsecase ??
      (this.disconnectedTransferCallUsecase = new DisconnectedTransferCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildDisconnectedTransferCallPublisher(),
      ))
    );
  }

  private static connectedTransferCallWithCrmOps: ConnectedTransferCallWithCrmOps;
  static buildConnectedTransferCallWithCrmOps(): ConnectedTransferCallWithCrmOps {
    return (
      this.connectedTransferCallWithCrmOps ??
      (this.connectedTransferCallWithCrmOps =
        new ConnectedTransferCallWithCrmOps(
          CrmOpsFactory.buildSalesforceConnectedCallCrmOps(),
          RepositoryFactory.buildMemCallRepository(),
          PublisherFactory.buildConnectedTransferCallPublisher(),
        ))
    );
  }

  private static storeAgentlistUsecase: StoreAgentList;
  static buildStoreAgentListUsecase(): StoreAgentList {
    return (
      this.storeAgentlistUsecase ??
      (this.storeAgentlistUsecase = new StoreAgentList(
        RepositoryFactory.buildMemAgentRepository(),
        PublisherFactory.buildStoreAgentListPublisher(),
      ))
    );
  }

  private static showAgentListUsecase: ShowAgentList;
  static buildShowAgentListUsecase(): ShowAgentList {
    return (
      this.showAgentListUsecase ??
      (this.showAgentListUsecase = new ShowAgentList(
        RepositoryFactory.buildMemUserRepository(),
        RepositoryFactory.buildMemAgentRepository(),
        PublisherFactory.buildShowAgentListPublisher(),
      ))
    );
  }

  private static teammateAvailabilityChangedUsecase: TeammateAvailabilityChanged;
  static buildTeammateAvailabilityChangedUsecase(): TeammateAvailabilityChanged {
    return (
      this.teammateAvailabilityChangedUsecase ??
      (this.teammateAvailabilityChangedUsecase =
        new TeammateAvailabilityChanged(
          RepositoryFactory.buildMemAgentRepository(),
          PublisherFactory.buildTeammateAvailabilityChangedPublisher(),
        ))
    );
  }

  private static syncIntegrationSettingUpdateUsecase: SyncIntegrationSettingUpdateUsecase;
  static buildSyncIntegrationSettingUpdateUsecase(): SyncIntegrationSettingUpdateUsecase {
    return (
      this.syncIntegrationSettingUpdateUsecase ??
      (this.syncIntegrationSettingUpdateUsecase =
        new SyncIntegrationSettingUpdate(
          RepositoryFactory.buildMemIntegrationRepository(),
        ))
    );
  }

  private static recordCallUuidUsecase: RecordCallUuidUsecase;
  static buildRecordCallUuidUsecase(): RecordCallUuidUsecase {
    return (
      this.recordCallUuidUsecase ??
      (this.recordCallUuidUsecase = new RecordCallUuid())
    );
  }

  private static syncAgentStatus: SyncAgentStatus;
  static buildSyncAgentStatusUsecase(): SyncAgentStatus {
    return (
      this.syncAgentStatus ??
      (this.syncAgentStatus = new SyncAgentStatus(
        RepositoryFactory.buildMemAgentRepository(),
      ))
    );
  }

  private static swapCallUsecase: SwapCallUsecase;
  static buildSwapCallUsecase(): SwapCallUsecase {
    return (
      this.swapCallUsecase ??
      (this.swapCallUsecase = new SwapCall(
        RepositoryFactory.buildMemCallRepository(),
        PublisherFactory.buildSwapCallPublisher(),
      ))
    );
  }

  private static getSignedRecordingUrl: GetSignedRecordingUrlUsecase;
  static buildGetSignedRecordingUrlUsecase(): GetSignedRecordingUrlUsecase {
    return (
      this.getSignedRecordingUrl ??
      (this.getSignedRecordingUrl = new GetSignedRecordingUrl(
        integrationManagerHttpClient,
      ))
    );
  }

  private static MuteAgent: MuteAgentCallUsecase;
  static buildMuteAgentCallUsecase(): MuteAgentCallUsecase {
    return (
      this.MuteAgent ??
      (this.MuteAgent = new MuteAgent(
        PublisherFactory.buildMuteAgentPublisher(),
      ))
    );
  }

  private static supervisorListenUsecase: SupervisorListenUsecase;
  static buildSupervisorListenUsecase(): SupervisorListenUsecase {
    return (
      this.supervisorListenUsecase ??
      (this.supervisorListenUsecase = new SupervisorListen(
        PublisherFactory.buildSupervisorListenPublisher(),
      ))
    );
  }

  private static supervisorDisconnectUsecase: SupervisorDisconnectUsecase;
  static buildSupervisorDisconnectUsecase(): SupervisorDisconnectUsecase {
    return (
      this.supervisorDisconnectUsecase ??
      (this.supervisorDisconnectUsecase = new SupervisorDisconnect(
        PublisherFactory.buildSupervisorDisconnectPublisher(),
      ))
    );
  }

  private static FetchPrerecordedMessages: FetchPrerecordedMessagesUsecase;
  static buildFetchPrerecordedMessagesUsecase(): FetchPrerecordedMessagesUsecase {
    return (
      this.FetchPrerecordedMessages ??
      (this.FetchPrerecordedMessages = new FetchPrerecordedMessages(
        PublisherFactory.buildFetchPrerecordedMessagesPublisher(),
      ))
    );
  }

  private static VoiceMailDrop: VoiceMailDropUsecase;
  static buildVoiceMailDropUsecase(): VoiceMailDropUsecase {
    return (
      this.VoiceMailDrop ??
      (this.VoiceMailDrop = new VoiceMailDrop(
        PublisherFactory.buildVoiceMailDropPublisher(),
      ))
    );
  }
}
